import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { 
  FaClock, 
  FaCalendarAlt, 
  FaUsers, 
  FaStar,
  FaCheckCircle,
  FaBookOpen,
  FaCertificate,
  FaArrowRight,
  FaHeartbeat,
  FaStethoscope,
  FaMicroscope,
  FaDna,
  FaVial,
  FaXRay,
  FaBrain,
  FaTooth,
  FaEye
} from 'react-icons/fa';

const PageContainer = styled.div`
  margin-top: 200px;
  min-height: calc(100vh - 210px);
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);


  @media (max-width: 768px) {
    margin-top: 120px;
  }
`;

const HeroSection = styled.div`
  background: linear-gradient(135deg, #0a4d68 0%, #088395 50%, #0a2b3c 100%);
  color: white;
  padding: 4rem 5%;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50px;
    right: -50px;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(255, 217, 102, 0.1) 0%, transparent 70%);
    border-radius: 50%;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -50px;
    left: -50px;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(255, 217, 102, 0.1) 0%, transparent 70%);
    border-radius: 50%;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  margin-bottom: 1rem;
  position: relative;
  z-index: 2;
  
  span {
    color: #ffd966;
    display: block;
    font-size: 2rem;
    font-weight: 300;
    margin-top: 0.5rem;
  }
`;

const HeroDescription = styled.p`
  font-size: 1.2rem;
  max-width: 700px;
  margin: 0 auto 2rem;
  opacity: 0.95;
  position: relative;
  z-index: 2;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 4rem 5%;
`;

const SectionTitle = styled.h2`
  font-size: 2.2rem;
  color: #0a2b3c;
  margin-bottom: 2rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 80px;
    height: 4px;
    background: #ffd966;
    border-radius: 2px;
  }
`;

const Grid2Col = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
  
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Grid3Col = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ContentBlock = styled.div`
  p {
    color: #4a5f73;
    line-height: 1.8;
    margin-bottom: 1.5rem;
    font-size: 1.1rem;
  }
`;

const InfoCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(10, 77, 104, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(10, 77, 104, 0.1);
    border-color: #ffd966;
  }
`;

const IconWrapper = styled.div`
  font-size: 2.5rem;
  color: #ffd966;
  margin-bottom: 1.5rem;
`;

const CardTitle = styled.h3`
  color: #0a2b3c;
  font-size: 1.3rem;
  margin-bottom: 1rem;
  font-weight: 600;
`;

const CardText = styled.p`
  color: #5a6b7a;
  line-height: 1.6;
  font-size: 0.95rem;
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
`;

const ListItem = styled.li`
  margin-bottom: 0.8rem;
  color: #4a5f73;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  
  svg {
    color: #ffd966;
    font-size: 1rem;
  }
`;


const StatCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 10px;
  text-align: center;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(10, 77, 104, 0.05);
  
  .value {
    font-size: 2.2rem;
    font-weight: 700;
    color: #0a4d68;
    margin-bottom: 0.3rem;
  }
  
  .label {
    color: #5a6b7a;
    font-size: 0.9rem;
    text-transform: uppercase;
  }
`;

const ChapterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin: 2rem 0;
  
  @media (max-width: 800px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const ChapterCard = styled.div`
  background: #f8fafc;
  padding: 1rem;
  border-radius: 8px;
  text-align: center;
  border: 1px solid rgba(10, 77, 104, 0.05);
  
  .chapter {
    color: #0a4d68;
    font-weight: 700;
    margin-bottom: 0.3rem;
  }
  
  .desc {
    color: #5a6b7a;
    font-size: 0.8rem;
  }
`;

const CTASection = styled.div`
  background: linear-gradient(135deg, #0a4d68, #088395);
  border-radius: 20px;
  padding: 3rem;
  text-align: center;
  color: white;
  margin-top: 4rem;
  
  h3 {
    font-size: 2rem;
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.1rem;
    margin-bottom: 2rem;
    opacity: 0.9;
  }
`;

const CTAButton = styled(Link)`
  background: #ffd966;
  color: #0a4d68;
  border: none;
  padding: 1rem 3rem;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  
  &:hover {
    background: white;
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  }
`;

const DiagnosisCoding = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  

  const chapters = [
    { chapter: "1-10", desc: "Infectious & Parasitic" },
    { chapter: "11-35", desc: "Neoplasms" },
    { chapter: "36-45", desc: "Blood & Immune" },
    { chapter: "46-60", desc: "Endocrine" },
    { chapter: "61-75", desc: "Mental Health" },
    { chapter: "76-85", desc: "Nervous System" },
    { chapter: "86-95", desc: "Eye & Ear" },
    { chapter: "96-115", desc: "Circulatory System" },
    { chapter: "116-135", desc: "Respiratory System" },
    { chapter: "136-165", desc: "Digestive System" },
    { chapter: "166-190", desc: "Musculoskeletal" },
    { chapter: "191-210", desc: "Genitourinary" }
  ];

  return (
    <PageContainer>
      <HeroSection>
        <HeroTitle>
          Diagnosis Coding Training
          <span>Master ICD-10-CM Diagnosis Coding</span>
        </HeroTitle>
        <HeroDescription>
          Comprehensive training in ICD-10-CM diagnosis coding for all healthcare settings. 
          Learn to accurately code diseases, injuries, and conditions across all body systems.
        </HeroDescription>
      </HeroSection>

      <Container>
      

        <Grid2Col>
          <ContentBlock>
            <SectionTitle>Program Overview</SectionTitle>
            <p>
              Diagnosis coding is the foundation of medical coding. Accurate ICD-10-CM coding 
              ensures proper reimbursement, supports clinical research, and provides critical 
              data for healthcare quality improvement.
            </p>
            <p>
              This comprehensive program covers all 21 chapters of ICD-10-CM, coding guidelines, 
              official conventions, and real-world case studies. You'll learn to code with 
              confidence across all body systems and healthcare settings.
            </p>
          </ContentBlock>
          
          <InfoCard>
            <IconWrapper><FaHeartbeat /></IconWrapper>
            <CardTitle>Why Diagnosis Coding?</CardTitle>
            <List>
              <ListItem><FaCheckCircle /> Foundation of all medical coding</ListItem>
              <ListItem><FaCheckCircle /> Required for every healthcare setting</ListItem>
              <ListItem><FaCheckCircle /> High demand for skilled coders</ListItem>
              <ListItem><FaCheckCircle /> Essential for certification</ListItem>
              <ListItem><FaCheckCircle /> Remote work opportunities</ListItem>
            </List>
          </InfoCard>
        </Grid2Col>

        <SectionTitle>ICD-10-CM Chapters Covered</SectionTitle>
        <ChapterGrid>
          {chapters.map((item, index) => (
            <ChapterCard key={index}>
              <div className="chapter">Ch. {item.chapter}</div>
              <div className="desc">{item.desc}</div>
            </ChapterCard>
          ))}
        </ChapterGrid>

        <SectionTitle>What You'll Master</SectionTitle>
        <Grid3Col>
          <InfoCard>
            <IconWrapper><FaStethoscope /></IconWrapper>
            <CardTitle>Coding Guidelines</CardTitle>
            <CardText>Official coding conventions, chapter-specific guidelines, and coding clinic advice.</CardText>
          </InfoCard>
          <InfoCard>
            <IconWrapper><FaMicroscope /></IconWrapper>
            <CardTitle>Pathophysiology</CardTitle>
            <CardText>Understanding disease processes to ensure accurate code selection.</CardText>
          </InfoCard>
          <InfoCard>
            <IconWrapper><FaDna /></IconWrapper>
            <CardTitle>Specificity</CardTitle>
            <CardText>Laterality, episode of care, and seventh character requirements.</CardText>
          </InfoCard>
        </Grid3Col>

        <CTASection>
          <h3>Ready to Master Diagnosis Coding?</h3>
          <p>Join our next batch and become an expert ICD-10-CM coder</p>
          <CTAButton to="/register">
            Enroll Now <FaArrowRight />
          </CTAButton>
        </CTASection>
      </Container>
    </PageContainer>
  );
};

export default DiagnosisCoding;